import { env } from "$env/dynamic/public";

/**
 * Returns the fully qualified public CDN URL for a given media asset.
 * If PUBLIC_MEDIA_URL is set, returns `https://media.studioclickhouse.com/path`.
 * Otherwise falls back to serving locally from `/path`.
 */
export function mediaUrl(path: string | undefined | null): string {
  if (!path) return "";
  
  // If already an absolute URL (e.g. external CDN or https://), return as-is
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const base = env.PUBLIC_MEDIA_URL?.replace(/\/+$/, "") || "";
  const normalized = path.replace(/^\.+/, "");
  const cleanPath = normalized.startsWith("/") ? normalized : `/${normalized}`;

  if (!base) {
    return cleanPath;
  }

  return `${base}${cleanPath}`;
}

/**
 * Resolves the optimized preview version of an image or video asset.
 * Maps `/images/category/name.ext` -> `[PUBLIC_MEDIA_URL]/images/category/previews/name.webp`.
 * Falls back safely to `mediaUrl(path)` if already a preview or path is external.
 */
export function getPreviewUrl(path: string | undefined | null): string {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;

  // Separate query parameter if present (e.g. ?v=2 for cache-busting)
  const [cleanPath, query] = path.split("?");
  const querySuffix = query ? `?${query}` : "";

  // If already a preview, return resolved mediaUrl
  if (cleanPath.includes("/previews/")) {
    return mediaUrl(`${cleanPath}${querySuffix}`);
  }

  const lastSlash = cleanPath.lastIndexOf("/");
  if (lastSlash === -1) {
    return mediaUrl(`${cleanPath}${querySuffix}`);
  }

  const dir = cleanPath.substring(0, lastSlash);
  const file = cleanPath.substring(lastSlash + 1);
  const baseName = file.replace(/\.[^.]+$/, "");

  // Previews are stored in [dir]/previews/[baseName].webp
  const previewPath = `${dir}/previews/${baseName}.webp${querySuffix}`;
  return mediaUrl(previewPath);
}

/**
 * Resolves a comma-separated srcset string, converting each local URL to its R2 CDN equivalent.
 */
export function mediaSrcSet(srcset: string | undefined | null): string {
  if (!srcset) return "";
  return srcset
    .split(",")
    .map((part) => {
      const trimmed = part.trim();
      if (!trimmed) return "";
      const [url, descriptor] = trimmed.split(/\s+/);
      const fullUrl = mediaUrl(url);
      return descriptor ? `${fullUrl} ${descriptor}` : fullUrl;
    })
    .filter(Boolean)
    .join(", ");
}

